// FadedImage — plain <img> + a separate overlay <div> using CSS
// background-image linear-gradients (NOT mask-image) to fade an image's
// edge(s) into the section's solid background color. This avoids
// CSS mask-image/mask-composite entirely, which rendered an inconsistent
// visible seam ("stripe") in some browsers. The overlay gradient's opaque
// color must match the section's own background exactly so the fade reads
// as a seamless disappearance rather than a visible line.
//
// The parent controls sizing/position (e.g. absolute inset-0 half-band).

const DIRECTION = {
  left: "to right",
  right: "to left",
  top: "to bottom",
  bottom: "to top",
};

// One edge → a gradient that is fully opaque (bg color, hiding the photo)
// at the true edge, fading to fully transparent (photo fully visible) by
// `size` (a % of the image on that axis). Mirrors the softness curve
// previously used by the mask-image approach (transition tightened to
// ~60% of `size`).
function edgeGradient(direction, size, bg, fadeOpacity = 1) {
  const distance = size * 0.6;
  const mid = Math.round(distance * 0.6);
  const end = Math.round(distance);
  const midAlphaHex = Math.round(140 * fadeOpacity)
    .toString(16)
    .padStart(2, "0");
  return `linear-gradient(${DIRECTION[direction]}, ${bg} 0%, ${bg}${midAlphaHex} ${mid}%, ${bg}00 ${end}%)`;
}

export function FadedImage({
  src,
  alt = "",
  // Fade span per edge, as a percentage of the image on that axis.
  featherLeft,
  featherRight,
  featherTop,
  featherBottom,
  // Must match the section's own solid background color exactly.
  bg,
  objectPosition = "center",
  // Optional CSS filter (e.g. "brightness(1.05) contrast(1.04)") applied
  // only to the <img>, for subtle tonal harmony between adjacent photos.
  filter,
  // Optional strength multiplier (0-1) for the fade overlay's opacity.
  // Defaults to 1 (original behavior, unaffected for existing callers).
  fadeOpacity = 1,
  // Optional multiplier (e.g. 0.88 to shrink 12%) applied as a CSS
  // transform:scale() on the <img> itself, independent of object-fit's
  // automatic cover scale. Defaults to 1 (no change, fully backward
  // compatible for existing callers).
  imageScale = 1,
  loading = "lazy",
  className = "",
  testId,
}) {
  const layers = [];
  if (featherLeft != null) layers.push(edgeGradient("left", featherLeft, bg, fadeOpacity));
  if (featherRight != null) layers.push(edgeGradient("right", featherRight, bg, fadeOpacity));
  if (featherTop != null) layers.push(edgeGradient("top", featherTop, bg, fadeOpacity));
  if (featherBottom != null) layers.push(edgeGradient("bottom", featherBottom, bg, fadeOpacity));

  return (
    <div className={className}>
      <img
        src={src}
        alt={alt}
        loading={loading}
        data-testid={testId}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition,
          filter,
          transform: imageScale !== 1 ? `scale(${imageScale})` : undefined,
        }}
      />
      {layers.length > 0 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: layers.join(", ") }}
        />
      )}
    </div>
  );
}

export default FadedImage;
